import axios from 'axios';
import { apiUrl } from '../utils/constants.ts';

// Define interfaces (can be moved to a shared types file)
export interface UserProfile {
    walletAddress: string;
    username: string | null; // Backend should always provide a username (real or default)
    profilePicture: string;
    isDefault?: boolean;
}

export interface Contact {
    walletAddress: string;
    username: string;
    profilePicture: string;
}

export interface UserSuggestion {
    walletAddress: string;
    username: string;
}


/**
 * Fetches profile data for a given identifier (address or username).
 * Relies on the backend to return default data if profile not found for an address.
 */
export const fetchProfileData = async (identifier: string): Promise<UserProfile> => {
    console.log("[ProfileService] Fetching profile data for:", identifier);
    try {
        const response = await fetch(`${apiUrl}/api/profile/${identifier}`);
        console.log("[ProfileService] Fetch profile data response status:", response.status);

        // Backend handles 404 for addresses by returning default data with 200 OK or specific structure
        if (!response.ok) {
             // If backend sends 404 even for addresses, handle it or adjust backend
             if (response.status === 404) {
                 console.warn(`[ProfileService] Profile not found for identifier: ${identifier}. Backend did not provide default.`);
                 // Decide how to handle this - throw error, return null, or a frontend default?
                 // Throwing error might be best to indicate backend issue or non-existent user (if not address)
                 throw new Error(`Profile not found for ${identifier}`);
             }
             throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("[ProfileService] Received profile data:", data);

        // Ensure the received data conforms to the UserProfile interface
        if (!data || typeof data.walletAddress !== 'string' || typeof data.username !== 'string') {
             console.error("[ProfileService] Received invalid profile data structure:", data);
             throw new Error("Received invalid profile data structure from API.");
        }

        return {
            walletAddress: data.walletAddress,
            username: data.username, // Use username directly from API (should be real or default)
            profilePicture: data.profilePicture || 'profil1.jpg', // Use default if missing
            isDefault: data.isDefault || false, // Use isDefault flag from API
        };
    } catch (error) {
        console.error("Failed to fetch profile data:", error);
        // Re-throw the error to be handled by the calling component
        throw error;
    }
};

/**
 * Fetches the contact list for a given user address.
 * @param userAddress The wallet address of the user whose contacts to fetch.
 * @returns A promise that resolves to an array of Contact objects.
 */
export const fetchContacts = async (userAddress: string): Promise<Contact[]> => {
    console.log(`[ProfileService] Fetching contacts for: ${userAddress}`); // Keep log
    if (!userAddress) {
        console.warn("[ProfileService] fetchContacts called with no userAddress.");
        return []; // Return empty array if no address provided
    }
    try {
        const response = await fetch(`${apiUrl}/api/profile/contacts?walletAddress=${userAddress}`); // Use GET with query param
        console.log(`[ProfileService] Fetch contacts response status: ${response.status}`); // Log status

        if (!response.ok) {
            const errorBody = await response.text(); // Read error body as text
            console.error(`[ProfileService] Failed to fetch contacts. Status: ${response.status}, Body: ${errorBody}`);
            throw new Error(`Failed to fetch contacts: ${response.statusText} - ${errorBody}`);
        }

        const data: Contact[] = await response.json(); // Expecting an array of Contact objects
        console.log(`[ProfileService] Received contacts data:`, data); // Log received data
        return data;
    } catch (error) {
        console.error("[ProfileService] Error in fetchContacts:", error);
        throw error; // Re-throw the error
    }
};

/**
 * Fetches all users for search suggestions.
 */
export const fetchAllUsers = async (): Promise<UserSuggestion[]> => {
    console.log("[ProfileService] Fetching all users for suggestions...");
    try {
        const response = await fetch(`${apiUrl}/api/profile/all-users`);
        console.log("[ProfileService] Fetch all users response status:", response.status);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        console.log("[ProfileService] Received all users data:", data);
        return data || [];
    } catch (error) {
        console.error("Failed to fetch all users for suggestions:", error);
        return [];
    }
};

/**
 * Adds a contact for the connected user.
 */
export const addContact = async (connectedAddress: string, contactAddress: string): Promise<{ success: boolean; error?: string }> => {
    console.log(`[ProfileService] Adding contact: ${contactAddress} for user: ${connectedAddress}`);
    try {
        const response = await fetch(`${apiUrl}/api/profile/contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ walletAddress: connectedAddress, contactAddress: contactAddress }),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data.error || 'Failed to add contact');
        }
        console.log("[ProfileService] Add contact successful.");
        return { success: true };
    } catch (error: any) {
        console.error("Error adding contact:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Removes a contact for the connected user.
 */
export const removeContact = async (connectedAddress: string, contactAddressToRemove: string): Promise<{ success: boolean; error?: string }> => {
    console.log(`[ProfileService] Removing contact: ${contactAddressToRemove} for user: ${connectedAddress}`);
    try {
        const response = await fetch(`${apiUrl}/api/profile/contact/${contactAddressToRemove}?walletAddress=${connectedAddress}`, {
            method: 'DELETE',
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data.error || 'Failed to remove contact');
        }
        console.log("[ProfileService] Remove contact successful.");
        return { success: true };
    } catch (error: any) {
        console.error("Error removing contact:", error);
        return { success: false, error: error.message };
    }
};

// Placeholder for fetching deployed tokens by a user
export const fetchDeployedTokens = async (userAddress: string): Promise<any[]> => {
    console.log(`[profileService] fetchDeployedTokens called for ${userAddress}. Placeholder implementation.`);
    // TODO: Implement actual API call to backend endpoint for deployed tokens
    // Example:
    // try {
    //     const response = await axios.get(`${apiUrl}/profile/${userAddress}/deployed-tokens`); // Adjust endpoint as needed
    //     return response.data;
    // } catch (error) {
    //     console.error(`Error fetching deployed tokens for ${userAddress}:`, error);
    //     return []; // Return empty array on error
    // }
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network delay
    return []; // Return empty array for now
};

// Placeholder for fetching trade history for a user
export const fetchTradeHistory = async (userAddress: string): Promise<any[]> => {
    console.log(`[profileService] fetchTradeHistory called for ${userAddress}. Placeholder implementation.`);
    // TODO: Implement actual API call to backend endpoint for trade history
    // Example:
    // try {
    //     const response = await axios.get(`${apiUrl}/profile/${userAddress}/trades`); // Adjust endpoint as needed
    //     return response.data;
    // } catch (error) {
    //     console.error(`Error fetching trade history for ${userAddress}:`, error);
    //     return []; // Return empty array on error
    // }
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network delay
    return []; // Return empty array for now
};

// Placeholder/Export for ensureInternalWallet if needed by Profile.tsx
export const ensureInternalWallet = async (userAddress: string): Promise<{ success: boolean; internalWalletAddress?: string; error?: string }> => {
    console.log(`[profileService] ensureInternalWallet called for ${userAddress}. Placeholder implementation.`);
     try {
         const response = await axios.post(`${apiUrl}/profile/ensure-internal-wallet`, { walletAddress: userAddress }); // Assuming endpoint exists
         return response.data;
     } catch (error: any) {
         console.error(`Error ensuring internal wallet for ${userAddress}:`, error);
         const errorMessage = error.response?.data?.error || error.message || "Failed to ensure internal wallet.";
         return { success: false, error: errorMessage };
     }
};

// --- NEW: Interface for Withdrawal History Record ---
export interface WithdrawalRecord {
    _id: string; // From MongoDB
    userId: string;
    amount: number;
    symbol: string;
    status: 'completed' | 'pending' | 'failed';
    transactionHash?: string;
    blockchainNetwork?: string;
    withdrawalAddress: string;
    timestamp: string; // ISO Date string
    remarks?: string;
}

export interface PaginatedWithdrawalHistory {
    data: WithdrawalRecord[];
    pagination: {
        currentPage: number;
        totalPages: number;
        totalRecords: number;
        limit: number;
    };
}

/**
 * Fetches the withdrawal history for a given user address.
 * @param userAddress The wallet address of the user whose withdrawal history to fetch.
 * @param page The page number for pagination.
 * @param limit The number of records per page.
 * @returns A promise that resolves to an object containing withdrawal records and pagination info.
 */
export const fetchWithdrawalHistory = async (userAddress: string, page: number = 1, limit: number = 10): Promise<PaginatedWithdrawalHistory> => {
    console.log(`[ProfileService] Fetching withdrawal history for: ${userAddress}, page: ${page}, limit: ${limit}`);
    if (!userAddress) {
        console.warn("[ProfileService] fetchWithdrawalHistory called with no userAddress.");
        // Return a default empty paginated structure
        return { 
            data: [], 
            pagination: { currentPage: 1, totalPages: 0, totalRecords: 0, limit: limit }
        };
    }
    try {
        const response = await fetch(`${apiUrl}/api/profile/${userAddress}/withdrawal-history?page=${page}&limit=${limit}`);
        console.log(`[ProfileService] Fetch withdrawal history response status: ${response.status}`);

        if (!response.ok) {
            const errorBody = await response.text();
            console.error(`[ProfileService] Failed to fetch withdrawal history. Status: ${response.status}, Body: ${errorBody}`);
            throw new Error(`Failed to fetch withdrawal history: ${response.statusText} - ${errorBody}`);
        }

        const result: PaginatedWithdrawalHistory = await response.json(); 
        console.log(`[ProfileService] Received withdrawal history data:`, result);
        if (!result || !result.data || !result.pagination) {
            console.error("[ProfileService] Received invalid withdrawal history data structure:", result);
            throw new Error("Received invalid withdrawal history data structure from API.");
        }
        return result;
    } catch (error) {
        console.error("[ProfileService] Error in fetchWithdrawalHistory:", error);
        // Re-throw to allow component to handle or return a default empty paginated structure
        // throw error;
        return { 
            data: [], 
            pagination: { currentPage: page, totalPages: 0, totalRecords: 0, limit: limit }
        };
    }
};
